/**
 * Component resolver + layout engine.
 *
 * Maps analyzed nodes to renderer kinds, groups them into titled sections,
 * orders everything by importance, and assigns progressive disclosure:
 * high-importance sections render immediately, the rest collapse behind
 * "More details", and telemetry stays hidden.
 */

import type {
  TConcept,
  TDataType,
  TDisclosure,
  TRenderBlock,
  TRendererKind,
  TRenderSection,
  TSemanticNode,
} from "./types";

const SECONDARY_THRESHOLD = 55;

/** Concepts clustered into one section when they arrive as loose leaf fields. */
const CONCEPT_SECTION_GROUPS: { id: string; title: string; concepts: TConcept[] }[] = [
  { id: "focus", title: "Investment Focus", concepts: ["stage", "industry", "geography", "market"] },
  { id: "financials", title: "Financials", concepts: ["revenue", "financial", "fundraising", "checkSize"] },
  { id: "profile", title: "Profile", concepts: ["identity", "social", "documents"] },
  { id: "strategy", title: "Strategy", concepts: ["thesis", "businessModel", "product", "technology", "moat"] },
  { id: "people", title: "Team", concepts: ["team"] },
  { id: "evidence", title: "Extracted Facts", concepts: ["facts"] },
  { id: "other", title: "More Information", concepts: ["general", "preferences", "operations", "governance", "traction", "idealCustomer", "valueAdd", "trackRecord", "risk", "competition", "portfolio"] },
];

function sectionGroupFor(concept: TConcept): { id: string; title: string } {
  const group = CONCEPT_SECTION_GROUPS.find((g) => g.concepts.includes(concept));
  return group ?? { id: "other", title: "More Information" };
}

/** Data types the KPI grid already represents — skipped as section blocks. */
const METRIC_TYPES: TDataType[] = ["currency", "currencyRange", "percentage"];

export function resolveRenderer(node: TSemanticNode): TRendererKind | null {
  switch (node.dataType) {
    case "tagList":
      return node.concept === "facts" ? "factsList" : "tagGroup";
    case "textList":
      return node.concept === "facts" ? "factsList" : "checklist";
    case "longText":
      return "expandableText";
    case "shortText":
      return "expandableText";
    case "objectList":
      return "objectCards";
    case "enum":
    case "url":
    case "email":
    case "date":
    case "boolean":
    case "number":
      return "keyValue";
    default:
      return null;
  }
}

type TLayoutAccumulator = {
  sections: Map<string, TRenderSection>;
  hiddenCount: number;
  metricIds: Set<string>;
};

function pushBlock(acc: TLayoutAccumulator, sectionId: string, sectionTitle: string, concept: TConcept, node: TSemanticNode): void {
  const kind = resolveRenderer(node);
  if (!kind) return;

  const block: TRenderBlock = { id: node.path.join("."), kind, node };
  const existing = acc.sections.get(sectionId);
  if (existing) {
    existing.blocks.push(block);
    existing.importance = Math.max(existing.importance, node.importance);
  } else {
    acc.sections.set(sectionId, {
      id: sectionId,
      title: sectionTitle,
      concept,
      importance: node.importance,
      disclosure: "primary",
      blocks: [block],
    });
  }
}

function placeNode(acc: TLayoutAccumulator, node: TSemanticNode, sectionOverride?: { id: string; title: string; concept: TConcept }): void {
  if (node.concept === "meta" || node.dataType === "empty") {
    acc.hiddenCount += 1;
    return;
  }

  // Values already shown in the KPI grid don't repeat as section content
  if (METRIC_TYPES.includes(node.dataType) && acc.metricIds.has(node.path.join("."))) {
    return;
  }

  if (node.dataType === "object" && node.children) {
    // An object gets its own titled section; children flatten into it
    const section = sectionOverride ?? { id: node.path.join("."), title: node.label, concept: node.concept };
    for (const child of node.children) {
      placeNode(acc, child, section);
    }
    return;
  }

  const target = sectionOverride ?? (() => {
    const group = sectionGroupFor(node.concept);
    return { id: group.id, title: group.title, concept: node.concept };
  })();

  pushBlock(acc, target.id, target.title, target.concept, node);
}

export type TLayoutResult = {
  sections: TRenderSection[];
  hiddenCount: number;
};

/**
 * Build ordered, disclosure-tagged sections from analyzed nodes.
 * `consumedKeys` are top-level keys already rendered elsewhere (hero,
 * recommendations); `metricIds` are node paths shown in the KPI grid.
 */
export function buildLayout(nodes: TSemanticNode[], consumedKeys: Set<string>, metricIds: Set<string>): TLayoutResult {
  const acc: TLayoutAccumulator = { sections: new Map(), hiddenCount: 0, metricIds };

  for (const node of nodes) {
    if (consumedKeys.has(node.key)) continue;
    placeNode(acc, node);
  }

  const sections = [...acc.sections.values()]
    .filter((section) => section.blocks.length > 0)
    .map((section) => ({
      ...section,
      disclosure: (section.importance >= SECONDARY_THRESHOLD ? "primary" : "secondary") as TDisclosure,
      blocks: section.blocks.sort((a, b) => b.node.importance - a.node.importance),
    }))
    .sort((a, b) => b.importance - a.importance);

  return { sections, hiddenCount: acc.hiddenCount };
}
